import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowRight, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { InstallPWAModal, InstallPWAButton } from "@/components/app/InstallPWA";
import { Logo } from "@/components/app/Logo";

const Login = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    import("@/lib/api").then(({ api }) => {
      api.get("/setup/status")
        .then(({ data }) => { if (data?.needs_setup) setNeedsSetup(true); })
        .catch(() => {});
    });
  }, []);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  if (needsSetup) return <Navigate to="/setup" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Bem-vindo, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no login");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white relative overflow-hidden">
      {/* Mobile-only background image */}
      <div className="lg:hidden absolute inset-0 -z-0">
        <img
          src="https://images.unsplash.com/photo-1776261762000-bc9dfdf9c27b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJhY2tncm91bmQlMjBsaWdodHxlbnwwfHx8fDE3NzgzMjc1MDV8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/85 via-zinc-950/70 to-zinc-950/95" />
      </div>
      <Toaster position="top-right" />
      <InstallPWAModal openOnMount={true} />
      {/* Hero */}
      <div className="hidden lg:flex relative overflow-hidden bg-zinc-950 text-zinc-100">
        <img
          src="https://images.unsplash.com/photo-1776261762000-bc9dfdf9c27b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJhY2tncm91bmQlMjBsaWdodHxlbnwwfHx8fDE3NzgzMjc1MDV8MA&ixlib=rb-4.1.0&q=85"
          alt="Zeferius Pay"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 via-zinc-950/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <Logo variant="full" height={42} />
          </div>
          <div className="space-y-6 max-w-lg">
            <p className="text-xs tracking-[0.3em] uppercase text-blue-300/80">Customer Loyalty Suite</p>
            <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-[1.05]">
              Cobrança automática,<br/>fidelização inteligente.
            </h1>
            <p className="text-zinc-300 text-base leading-relaxed">
              Tenha o controle total dos seus clientes, vencimentos, descontos progressivos e níveis de fidelidade — Bronze, Silver, Gold e King.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            <div className="border border-white/10 rounded-xl p-4">
              <div className="metric-num text-2xl">-7d</div>
              <div className="text-[11px] tracking-widest uppercase text-zinc-400 mt-1">Lembrete</div>
            </div>
            <div className="border border-white/10 rounded-xl p-4">
              <div className="metric-num text-2xl text-amber-300">King</div>
              <div className="text-[11px] tracking-widest uppercase text-zinc-400 mt-1">Topo</div>
            </div>
            <div className="border border-white/10 rounded-xl p-4">
              <div className="metric-num text-2xl text-emerald-300">15%</div>
              <div className="text-[11px] tracking-widest uppercase text-zinc-400 mt-1">Desconto</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-md lg:bg-transparent">
          <div className="lg:hidden flex items-center gap-2 mb-10 justify-center">
            <Logo variant="full" height={56} />
          </div>
          {/* Card wrapper - solid on mobile, transparent on desktop */}
          <div className="lg:bg-transparent bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-0 lg:shadow-none shadow-2xl border border-white/40 lg:border-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Acesso</p>
            <InstallPWAButton />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Entrar na sua conta</h2>
          <p className="text-zinc-600 mt-2">Use seu usuário e senha de cliente ou administrador.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs tracking-widest uppercase text-zinc-500">Usuário ou e-mail</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ex: seu nome"
                className="mt-2 h-11"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-zinc-500">Senha</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {busy ? "Entrando..." : (<>Entrar <ArrowRight className="ml-2" size={18} weight="bold"/></>)}
            </Button>

            <Card className="mt-4 border-zinc-200 bg-zinc-50/60 p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="text-blue-600 mt-0.5" size={18} weight="duotone" />
                <div className="text-sm text-zinc-600">
                  Seus dados são criptografados e protegidos. Em caso de dúvida, fale com o suporte pelo botão flutuante no canto da tela.
                </div>
              </div>
            </Card>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
